import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Newspaper, ArrowRight, Sparkles } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { NewsArticle, HomepageLayoutConfig } from '../../types/news';
import { newsService, DEFAULT_HOMEPAGE_LAYOUT } from '../../lib/newsService';
import { NewsCard } from '../news/NewsCard';

export const HomeNewsStrip: React.FC = () => {
  const { language } = useLanguage();
  const isFr = language === 'fr';
  const navigate = useNavigate();

  const [config, setConfig] = useState<HomepageLayoutConfig>(DEFAULT_HOMEPAGE_LAYOUT);
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadData() {
      try {
        const [loadedConfig, loadedArticles] = await Promise.all([
          newsService.getHomepageLayoutConfig(),
          newsService.getPublishedArticles(),
        ]);

        if (isMounted) {
          // Strict human-approval enforcement: No blog post is shown on home page without explicit human approval
          const humanApproved = loadedArticles.filter(
            (a) => a.status === 'published' && a.human_approved === true
          );
          setConfig(loadedConfig);
          setArticles(humanApproved);
          setIsLoaded(true);
        }
      } catch (err) {
        console.warn('[HOME NEWS STRIP] Data load notice:', err);
        if (isMounted) {
          setIsLoaded(true);
        }
      }
    }

    loadData();

    return () => {
      isMounted = false;
    };
  }, []);

  // 1. If turned off in Admin Homepage Layout screen, do not render
  if (isLoaded && !config.newsStripEnabled) {
    return null;
  }

  // 2. Hide automatically if there are no published articles
  if (isLoaded && articles.length === 0) {
    return null;
  }

  // 3. Process Pinned Article & Slice limit
  const maxToShow = Math.max(3, Math.min(6, config.maxArticlesToShow || 4));
  let displayArticles: NewsArticle[] = [];

  if (config.pinnedArticleId) {
    const pinned = articles.find((a) => a.id === config.pinnedArticleId);
    if (pinned) {
      const rest = articles.filter((a) => a.id !== config.pinnedArticleId);
      displayArticles = [{ ...pinned, is_pinned: true }, ...rest].slice(0, maxToShow);
    } else {
      displayArticles = articles.slice(0, maxToShow);
    }
  } else {
    displayArticles = articles.slice(0, maxToShow);
  }

  if (displayArticles.length === 0) {
    return null;
  }

  const allNewsUrl = isFr ? '/fr/actualites' : '/en/news';
  const sectionTitle = isFr
    ? config.sectionTitleFr || 'Dernières Actualités & Analyses du Marché Foncier'
    : config.sectionTitleEn || 'Latest Real Estate News & Market Insights';

  return (
    <section className="bg-slate-50/60 border-y border-slate-200/60 py-12">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100/80 text-emerald-900 border border-emerald-200/60 text-xs font-bold tracking-wide uppercase">
              <Newspaper className="w-3.5 h-3.5 text-emerald-700" />
              <span>{isFr ? 'Actualités & Tendances' : 'Market Intelligence'}</span>
            </div>

            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-black text-slate-900 tracking-tight">
              {sectionTitle}
            </h2>

            <p className="text-sm sm:text-base text-slate-600 max-w-2xl leading-relaxed">
              {isFr
                ? 'Cadastres numériques MINDCAF, évolution des prix au m², réformes notariales et opportunités foncières à fort potentiel au Cameroun.'
                : 'MINDCAF digital cadastre rollout, square-meter value movements, notarial procedures, and high-yield Cameroon land opportunities.'}
            </p>
          </div>

          {/* "See all news" Button */}
          <div className="shrink-0">
            <Link
              to={allNewsUrl}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-white hover:bg-slate-900 text-slate-800 hover:text-white border border-slate-200 hover:border-slate-900 font-bold text-xs sm:text-sm shadow-xs hover:shadow-md transition-all duration-200 group cursor-pointer"
            >
              <span>{isFr ? 'Toutes les actualités' : 'See all news'}</span>
              <ArrowRight className="w-4 h-4 text-emerald-600 group-hover:text-white transition-colors" />
            </Link>
          </div>
        </div>

        {/* Thumbnail Cards Grid: 4 cols on desktop, 2 on tablet, 1 on mobile with horizontal swipe */}
        <div className="flex overflow-x-auto snap-x snap-mandatory sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-6 no-scrollbar pb-3 pt-1 -mx-4 px-4 sm:mx-0 sm:px-0">
          {displayArticles.map((article) => {
            const isArticlePinned = Boolean(
              article.is_pinned || (config.pinnedArticleId && article.id === config.pinnedArticleId)
            );

            return (
              <div
                key={article.id}
                className="snap-start shrink-0 w-[84vw] sm:w-auto"
              >
                <NewsCard
                  article={article}
                  isFr={isFr}
                  isPinned={isArticlePinned}
                  onClick={() => navigate(`${allNewsUrl}/${article.slug}`)}
                  className="h-full"
                />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
